import _sortBy from "lodash/sortBy"
import {
  type LucideIcon,
  Box,
  BriefcaseBusiness,
  CircleHelp,
  FileLineChart,
  FileText,
  House,
  Map,
  PanelBottom,
  SquareGanttChart,
} from "lucide-react"
import { useTranslation } from "react-i18next"

import useUserStore from "stores/user"

export type MenuItemType = {
  key: string
  hide?: boolean
  label: string
  isTitle?: boolean
  hideForCC?: boolean
  hideForAnyCC?: boolean
  showForCustomer?: boolean
  hideForExternal?: boolean
  showDriver?: boolean
  adminOnly?: boolean
  priceOnly?: boolean
  icon?: LucideIcon
  onClick?: any
  url?: string
  badge?: {
    variant: string
    text: string
  }
  parentKey?: string
  target?: string
  children?: MenuItemType[]
}

const useMenuItemsWithFlags = (flags: any) => {
  const { t } = useTranslation()
  const hideLinks = useUserStore((state) => state?.hideLinks)
  const hasPricePermission = useUserStore((state) => state?.hasPricePermission)
  const isCustomer = useUserStore((state) => state?.isCustomer)

  const {
    driverLocationUpdateEnabled,
    showBestBuy,
    omniReportSidebar,
    usePagesV2,
  } = flags || {}

  const COMPANY_MANAGEMENT_CHILDREN = _sortBy(
    [
      {
        key: "trucks",
        label: t("navigation.companyManagement.trucks"),
        adminOnly: true,
        url: usePagesV2 ? "/app/trucks" : "/trucks",
        parentKey: "company-management-menu",
      },
      {
        key: "trailers",
        label: t("navigation.companyManagement.trailers"),
        adminOnly: true,
        url: usePagesV2 ? "/app/trailers" : "/trailers",
        parentKey: "company-management-menu",
      },
      {
        key: "users",
        label: t("navigation.companyManagement.users"),
        isTitle: false,
        url: "/drivers",
        parentKey: "company-management-menu",
      },
      {
        key: "groups",
        label: t("navigation.companyManagement.groups"),
        isTitle: false,
        url: "/groups",
        parentKey: "company-management-menu",
      },
      {
        key: "common-carriers",
        label: t("navigation.companyManagement.commonCarriers"),
        isTitle: false,
        adminOnly: true,
        url: usePagesV2 ? "/app/common-carriers" : "/common-carriers",
        parentKey: "company-management-menu",
      },
      {
        key: "customers",
        label: t("navigation.companyManagement.customers"),
        url: usePagesV2 ? "/app/customers" : "/customers",
        hideForExternal: true,
        adminOnly: true,
        parentKey: "company-management-menu",
      },
      {
        key: "freight-bills-setup",
        adminOnly: true,
        label: t("navigation.companyManagement.freightBillsSetup"),
        url: "/freight_billing_setup",
        parentKey: "company-management-menu",
      },
      {
        key: "driver-pay-rate",
        label: t("navigation.companyManagement.driverPayRateSetup"),
        adminOnly: true,
        url: "/driver_pay_setup",
        parentKey: "company-management-menu",
      },
      {
        key: "loading_numbers",
        label: t("navigation.companyManagement.loadingNumbers"),
        adminOnly: true,
        url: usePagesV2 ? "/app/loading-numbers" : "/loading_numbers",
        parentKey: "company-management-menu",
      },
      {
        key: "products",
        label: t("navigation.companyManagement.products"),
        adminOnly: true,
        url: "/products",
        parentKey: "company-management-menu",
      },
      {
        key: "tax",
        adminOnly: true,
        label: t("navigation.companyManagement.taxSetup"),
        url: "/tax",
        parentKey: "company-management-menu",
      },
      {
        key: "suppliers",
        adminOnly: true,
        label: t("navigation.companyManagement.suppliers"),
        url: "/suppliers",
        parentKey: "company-management-menu",
      },
      {
        key: "terminals",
        adminOnly: true,
        label: t("navigation.companyManagement.terminals"),
        url: usePagesV2 ? "/app/terminals" : "/terminals",
        parentKey: "company-management-menu",
      },
      {
        key: "contracts-management",
        label: t("navigation.companyManagement.contractManagement"),
        hideForCC: true,
        showForCustomer: true,
        url: "/contracts",
        parentKey: "company-management-menu",
      },
    ],
    "label"
  )

  const MENU_ITEMS: MenuItemType[] = [
    {
      key: "dispatch-center",
      label: t("navigation.dispatchCenter"),
      isTitle: false,
      showDriver: true,
      icon: House,
      url: "/",
    },
    {
      key: "map-view",
      hide: !driverLocationUpdateEnabled,
      label: t("navigation.mapView"),
      isTitle: false,
      hideForAnyCC: true,
      icon: Map,
      url: "/map-view",
    },
    {
      key: "inventory",
      label: t("navigation.inventoryManagement"),
      isTitle: false,
      hideForCC: true,
      showForCustomer: true,
      icon: PanelBottom,
      url: "/inventory",
    },
    {
      key: "dispatching-views",
      label: t("navigation.dispatchingViews.title"),
      hideForCC: true,
      showForCustomer: true,
      showDriver: true,
      hideForAnyCC: true,
      isTitle: false,
      icon: SquareGanttChart,
      children: [
        {
          key: "daily-driver-schedule",
          showForCustomer: true,
          showDriver: true,
          label: t("navigation.dispatchingViews.dailyDriverSchedule"),
          url: "/",
          parentKey: "dispatching-views",
        },
        {
          key: "load-list",
          showForCustomer: false,
          showDriver: true,
          label: t("navigation.dispatchingViews.loadList"),
          url: "/loads",
          parentKey: "dispatching-views",
        },
        {
          key: "load-tracking",
          showDriver: true,
          showForCustomer: false,
          label: t("navigation.dispatchingViews.loadTracking"),
          url: "/tracking",
          parentKey: "dispatching-views",
        },
        {
          key: "mobile-fueling",
          showDriver: false,
          showForCustomer: false,
          label: t("navigation.dispatchingViews.mobileFueling"),
          url: "/mobile-fueling",
          parentKey: "dispatching-views",
        },
        {
          key: "scheduled-loads",
          showDriver: true,
          showForCustomer: false,
          label: t("navigation.dispatchingViews.scheduledLoads"),
          url: "/scheduled-loads",
          parentKey: "dispatching-views",
        },
      ],
    },
    {
      key: "reports",
      label: t("navigation.reports.title"),
      hideForCC: true,
      showForCustomer: true,
      hideForAnyCC: true,
      isTitle: false,
      icon: FileLineChart,
      children: [
        {
          key: "driver-hours",
          showDriver: true,
          showForCustomer: false,
          label: t("navigation.reports.driverHours"),
          url: "/driver-hours",
          parentKey: "reports",
        },
        {
          key: "pre-post-trip-reports",
          showDriver: true,
          showForCustomer: false,
          label: t("navigation.reports.prePostTripReports"),
          url: "/pre-post-trip-reports",
          parentKey: "reports",
        },
        {
          key: "wholesale-overview",
          hide: !omniReportSidebar,
          showForCustomer: true,
          label: t("navigation.reports.wholesaleOverview"),
          url: "/dashboards/wholesale-overview",
          parentKey: "reports",
        },
        {
          key: "deliveries-new",
          hide: !omniReportSidebar,
          showForCustomer: true,
          label: t("navigation.reports.deliveriesNew"),
          url: "/dashboards/deliveries",
          parentKey: "reports",
        },
        {
          key: "search",
          showForCustomer: true,
          label: t("navigation.reports.search"),
          url: "/deliveries/",
          parentKey: "reports",
        },
        {
          key: "scorecard",
          showForCustomer: true,
          adminOnly: true,
          priceOnly: true,
          label: t("navigation.reports.scorecard"),
          url: "/scorecard/",
          parentKey: "reports",
        },
        {
          key: "site-export",
          showForCustomer: true,
          hideForAnyCC: true,
          label: t("navigation.reports.siteExport"),
          url: "/site-export/",
          parentKey: "reports",
        },
      ],
    },
    {
      hide: !showBestBuy && (!hasPricePermission || !isCustomer),
      key: "supply-management",
      label: t("navigation.supplyManagement.title"),
      isTitle: false,
      hideForCC: true,
      hideForAnyCC: true,
      showForCustomer: hasPricePermission,
      icon: Box,
      children: [
        {
          key: "supply-overview",
          label: t("navigation.supplyManagement.supplyOverview"),
          url: "/supply-management/overview",
          parentKey: "supply-management",
        },
        {
          key: "pricing",
          label: t("navigation.supplyManagement.pricingImport"),
          url: "/pricing",
          parentKey: "supply-management",
        },
        {
          key: "custom-suppliers",
          label: t("navigation.supplyManagement.customSuppliers"),
          hideForCC: true,
          adminOnly: true,
          url: "/custom_suppliers",
          parentKey: "supply-management",
        },
        {
          key: "email-notifications",
          label: t("navigation.supplyManagement.emailNotifications"),
          url: "/email-notifications",
          parentKey: "supply-management",
        },
        {
          key: "quotations",
          label: t("navigation.supplyManagement.quoting"),
          hideForExternal: true,
          url: "/quotations",
          parentKey: "supply-management",
        },
        {
          key: "quoting_history",
          label: t("navigation.supplyManagement.quotingHistory"),
          showForCustomer: hasPricePermission,
          url: "/quoting_history",
          parentKey: "supply-management",
        },
        {
          key: "planner",
          label: t("navigation.supplyManagement.customerLocationBestBuy"),
          url: "/planner",
          parentKey: "supply-management",
        },
        {
          key: "arbitrage-map",
          label: t("navigation.supplyManagement.arbitrageMap"),
          url: "/supply-management/arbitrage-map",
          parentKey: "supply-management",
        },
      ],
    },
    {
      hide: hideLinks,
      key: "paperwork",
      label: t("navigation.paperwork.title"),
      hideForCC: true,
      hideForAnyCC: true,
      showForCustomer: true,
      isTitle: false,
      icon: FileText,
      children: [
        {
          key: "driver-pay",
          label: t("navigation.paperwork.driverPay"),
          url: "/driver-pay",
          parentKey: "paperwork",
        },
        {
          key: "freight-bills",
          label: isCustomer
            ? t("navigation.paperwork.completedDeliveries")
            : t("navigation.paperwork.invoices"),
          showForCustomer: true,
          url: "/freight_billing",
          parentKey: "paperwork",
        },
        {
          key: "reconciliation",
          label: t("navigation.paperwork.reconciliation"),
          url: "/reconciliation",
          parentKey: "paperwork",
        },
      ],
    },
    {
      hide: hideLinks,
      key: "company-management-menu",
      label: t("navigation.companyManagement.title"),
      isTitle: false,
      icon: BriefcaseBusiness,
      children: COMPANY_MANAGEMENT_CHILDREN,
    },
    {
      key: "support",
      label: t("navigation.support"),
      showDriver: true,
      isTitle: false,
      icon: CircleHelp,
      url: "",
      onClick: (e: any) => {
        window.location.href = "mailto:support@getdashfuel.com"
        e?.preventDefault?.()
      },
    },
  ]
  return MENU_ITEMS
}

export const useMenuItems = (flags: any) => {
  return useMenuItemsWithFlags(flags)
}

export const findAllParent = (
  menuItems: MenuItemType[],
  menuItem: MenuItemType
): string[] => {
  let parents: string[] = []
  const parent = findMenuItem(menuItems, menuItem["parentKey"])

  if (parent) {
    parents.push(parent["key"])
    if (parent["parentKey"])
      parents = [...parents, ...findAllParent(menuItems, parent)]
  }

  return parents
}

export const findMenuItem = (
  menuItems: MenuItemType[] | undefined,
  menuItemKey: MenuItemType["key"] | undefined
): MenuItemType | null => {
  if (menuItems && menuItemKey) {
    for (let i = 0; i < menuItems.length; i++) {
      if (menuItems[i].key === menuItemKey) {
        return menuItems[i]
      }
      const found = findMenuItem(menuItems[i].children, menuItemKey)
      if (found) return found
    }
  }
  return null
}
